import React, { useEffect, useMemo } from 'react';
import { Alert, Col, Form, Input, InputNumber, Row, Select, Switch, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { useCustomerSegmentOptions } from '../../../../hooks/options/useCustomerSegmentOptions';
import type { ContactCategoryDetail, ContactCategoryFormValues } from '../types';

const { Title } = Typography;

export interface ContactCategoryFormProps {
  form: FormInstance<ContactCategoryFormFieldValues>;
  mode: 'add' | 'edit';
  initialValues?: ContactCategoryDetail | null;
  onFinish: (values: ContactCategoryFormValues) => void;
}

export interface ContactCategoryFormFieldValues extends ContactCategoryFormValues {}

function detailToFormValues(d: ContactCategoryDetail): ContactCategoryFormFieldValues {
  return {
    label: d.label,
    sortOrder: d.sortOrder,
    customerSegmentId: d.customerSegmentId,
    isActive: d.isActive,
  };
}

const ContactCategoryForm: React.FC<ContactCategoryFormProps> = ({
  form,
  mode,
  initialValues,
  onFinish,
}) => {
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });

  const segmentOptions = useMemo(
    () =>
      segments.map((s) => ({
        value: s.id,
        label: s.label,
      })),
    [segments],
  );

  const canChangeSegment = mode === 'add' || (initialValues?.canChangeSegment ?? true);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(detailToFormValues(initialValues));
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: true, sortOrder: 0 });
    }
  }, [initialValues, form]);

  return (
    <Form<ContactCategoryFormFieldValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      {!canChangeSegment && (
        <Alert
          type="info"
          showIcon
              message="Customer segment is locked because this category has active leads in open stages."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Details
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="label"
              label="Label"
              rules={[{ required: true, message: 'Label is required' }]}
            >
              <Input placeholder="e.g. School" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="sortOrder"
              label="Sort order"
              rules={[{ required: true, message: 'Sort order is required' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="customerSegmentId"
              label="Customer segment"
              rules={[{ required: true, message: 'Customer segment is required' }]}
            >
              <Select
                options={segmentOptions}
                placeholder="Select segment"
                disabled={!canChangeSegment}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        {mode === 'edit' && initialValues?.code && (
          <Typography.Text type="secondary">Code: {initialValues.code}</Typography.Text>
        )}
      </div>
    </Form>
  );
};

export default ContactCategoryForm;
